import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import TierBadge from "@/components/tier-badge";
import { Gift, Copy, X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Rewards() {
  const { t, lang } = useLang();
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [popup, setPopup] = useState(null);

  const load = async () => {
    try {
      const [rw, st] = await Promise.all([api.get("/rewards"), api.get("/dashboard/stats")]);
      setRewards(rw.data);
      setStats(st.data);
      // Detect unacknowledged sent Amazon gift card -> show popup
      const unseen = rw.data.find(
        (r) => r.status === "sent" && r.type === "amazon" && !r.acknowledged,
      );
      if (unseen) setPopup(unseen);
    } catch (_) { /* ignore load errors */ }
  };
  useEffect(() => { load(); }, []);

  const wallet = stats?.wallet || { available: 0, pending: 0, lifetime: 0 };

  const copyCode = async (code) => {
    await navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  const dismissPopup = async () => {
    if (popup) {
      try { await api.post("/rewards/ack", { reward_id: popup.id }); } catch (_) { /* non-blocking ack */ }
      setPopup(null);
      load();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif-display text-4xl">{t.rewards.title}</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <WalletCard testid="wallet-available" label={t.rewards.available} value={`£${wallet.available}`} accent />
        <WalletCard testid="wallet-pending" label={t.rewards.pending} value={`£${wallet.pending}`} />
        <WalletCard testid="wallet-lifetime" label={t.rewards.lifetime} value={`£${wallet.lifetime}`} />
      </div>

      <div className="rounded-3xl bg-white border border-[#e6dfd2] p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif-display text-2xl">{t.rewards.history}</h2>
          <TierBadge level={stats?.level || "Starter"} />
        </div>
        {rewards.length === 0 ? (
          <div className="py-12 text-center text-sm text-[#6b7280]">{t.rewards.empty}</div>
        ) : (
          <div className="mt-5 space-y-3">
            {rewards.map((r) => (
              <div key={r.id} data-testid={`reward-item-${r.id}`} className="p-5 rounded-2xl border border-[#e6dfd2] hover:border-[#0d3b66]/30 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${r.type === "bonus" ? "bg-[#e07a5f]/20" : "bg-[#f4a261]/20"}`}>
                      <Gift size={22} weight="duotone" color="#0d3b66" />
                    </div>
                    <div>
                      <div className="font-serif-display text-xl">{lang === "tr" ? r.label_tr : r.label_en}</div>
                      <div className="text-xs text-[#6b7280] mt-1">
                        {r.type === "bonus" ? (
                          <span className="inline-flex items-center gap-1"><Sparkle size={10} weight="fill" color="#e07a5f" /> {t.rewards.bonusTitle}</span>
                        ) : (
                          <>Referral #{r.referral_number} · <TierBadge level={r.level} size="sm" /></>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-widest text-[#6b7280]">{t.rewards.status}</div>
                    <div className={`mt-1 text-sm font-medium ${r.status === "sent" ? "text-[#0d3b66]" : "text-[#e07a5f]"}`}>{r.status}</div>
                  </div>
                </div>
                {r.type === "bonus" && (
                  <div className="mt-3 text-xs text-[#6b7280]">{t.rewards.bonusHint}</div>
                )}
                {r.status === "sent" && r.type === "amazon" && r.amazon_code && (
                  <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-[#f6f2ea]">
                    <div className="text-xs uppercase tracking-widest text-[#6b7280]">{t.rewards.code}</div>
                    <code className="flex-1 text-sm">{r.amazon_code}</code>
                    <button data-testid={`copy-code-${r.id}`} onClick={() => copyCode(r.amazon_code)} className="text-[#0d3b66] hover:opacity-70"><Copy size={16} /></button>
                  </div>
                )}
                {r.status === "sent" && r.type === "hotel" && r.hotel_booking_ref && (
                  <div className="mt-4 p-3 rounded-lg bg-[#f6f2ea] text-sm"><span className="text-[#6b7280] uppercase text-xs tracking-widest mr-2">{t.rewards.booking}</span>{r.hotel_booking_ref}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {popup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 relative fade-up">
            <button data-testid="popup-close-btn" onClick={dismissPopup} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#e6dfd2] flex items-center justify-center hover:border-[#0d3b66]"><X size={14} /></button>
            <div className="w-16 h-16 rounded-full bg-[#f4a261] flex items-center justify-center mx-auto">
              <Gift size={32} weight="duotone" color="#0d3b66" />
            </div>
            <h2 className="font-serif-display text-3xl text-center mt-6">{t.rewards.popupTitle}</h2>
            <p className="text-[#3b4b5a] text-center mt-3">{t.rewards.popupBody}</p>
            <button data-testid="popup-ok-btn" onClick={dismissPopup} className="w-full mt-6 px-6 py-3.5 rounded-full bg-[#0d3b66] text-[#f4a261] font-medium hover:bg-[#071f36]">
              {t.rewards.popupOk}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WalletCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-3xl p-6 border ${accent ? "bg-[#0d3b66] text-white border-[#0d3b66]" : "bg-white border-[#e6dfd2]"}`}>
      <div className={`text-xs uppercase tracking-widest ${accent ? "text-white/60" : "text-[#6b7280]"}`}>{label}</div>
      <div className={`font-serif-display text-4xl mt-3 ${accent ? "text-[#f4a261]" : ""}`}>{value}</div>
    </div>
  );
}
